import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ClientSelectionPanel = ({ 
  selectedClients, 
  onSelectAll, 
  onDeselectAll, 
  onRemoveClient,
  validationResults 
}) => {
  const getValidationIcon = (status) => {
    switch (status) {
      case 'valid':
        return { icon: 'CheckCircle2', color: 'var(--color-success)' };
      case 'warning':
        return { icon: 'AlertTriangle', color: 'var(--color-warning)' };
      case 'error':
        return { icon: 'XCircle', color: 'var(--color-error)' };
      default:
        return { icon: 'Clock', color: 'var(--color-muted-foreground)' };
    }
  };

  return (
    <div className="h-full flex flex-col bg-[var(--color-card)] rounded-lg border border-[var(--color-border)]">
      <div className="p-4 border-b border-[var(--color-border)]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[var(--color-foreground)]">
            Ausgewählte Kunden
          </h3>
          <span className="text-sm text-[var(--color-muted-foreground)]">
            {selectedClients?.length} ausgewählt
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="CheckSquare"
            iconPosition="left"
            onClick={onSelectAll}
            className="flex-1"
          >
            Alle auswählen
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Square"
            iconPosition="left"
            onClick={onDeselectAll}
            className="flex-1"
          >
            Alle abwählen
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {selectedClients?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-16 h-16 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-4">
              <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm text-[var(--color-muted-foreground)] mb-1">
              Keine Kunden ausgewählt
            </p>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Wählen Sie Kunden aus der Suche aus
            </p>
          </div>
        ) : (
          selectedClients?.map((client) => {
            const validation = validationResults?.[client?.id] || { status: 'pending' };
            const statusInfo = getValidationIcon(validation?.status);

            return (
              <div
                key={client?.id}
                className="p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked
                    onChange={() => onRemoveClient(client?.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-sm text-[var(--color-foreground)]">
                        {client?.firstName} {client?.lastName}
                      </h4>
                      <Icon
                        name={statusInfo?.icon}
                        size={16}
                        color={statusInfo?.color}
                      />
                    </div>
                    <p className="text-xs text-[var(--color-muted-foreground)] mb-1">
                      ID: {client?.clientId}
                    </p>
                    {validation?.message && (
                      <p className="text-xs text-[var(--color-muted-foreground)] line-clamp-2">
                        {validation?.message}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => onRemoveClient(client?.id)}
                    className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors"
                  >
                    <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      {selectedClients?.length > 0 && (
        <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-muted)]/30">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-[var(--color-success)]">
                {Object.values(validationResults)?.filter(v => v?.status === 'valid')?.length}
              </div>
              <div className="text-xs text-[var(--color-muted-foreground)]">Gültig</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-[var(--color-warning)]">
                {Object.values(validationResults)?.filter(v => v?.status === 'warning')?.length}
              </div>
              <div className="text-xs text-[var(--color-muted-foreground)]">Warnungen</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-[var(--color-error)]">
                {Object.values(validationResults)?.filter(v => v?.status === 'error')?.length}
              </div>
              <div className="text-xs text-[var(--color-muted-foreground)]">Fehler</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSelectionPanel;